import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useCart } from '../../contexts/CartContext';
import { useCartDrawer } from '../../contexts/CartDrawerContext';
import CartDrawer from '../cart/CartDrawer';
import ProfileMenu from './ProfileMenu';

export default function Header() {
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { isCartOpen, openCart, closeCart } = useCartDrawer();
  const { itemCount } = useCart();
  const { user, isAuthenticated, logout } = useAuth();

  const handleLogout = async () => {
    await logout();
    navigate('/restaurants');
  };

  const handleLoginClick = () => {
    navigate('/login');
    setIsMenuOpen(false);
  };

  return (
    <>
      <header className="bg-mina-orange shadow-mina sticky top-0 z-40">
        <div className="container-full ">
          <div className="flex items-center justify-between gap-3 sm:gap-4 h-16 sm:h-20 mx-8">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2 flex-shrink-0 group">
              <div className="relative">
                <img
                  src="/munchiesLogo-black.png"
                  alt="Neighbourhood Munchies"
                  className="h-10 w-10 sm:h-12 sm:w-12 transition-transform group-hover:scale-105"
                />
              </div>
              <div className="hidden sm:block">
                <h1 className="text-base sm:text-lg md:text-xl font-bold text-grey leading-tight">
                  Neighbourhood Munchies
                </h1>
              </div>
            </Link>

            {/* Right Side Actions */}
            <div className="flex items-center gap-2">
              {/* Cart Icon - Mobile & Tablet */}
              <button
                onClick={openCart}
                className="lg:hidden relative p-2 text-mina-espresso hover:bg-black/10 rounded-mina transition-all active:scale-95"
                aria-label={`Shopping cart with ${itemCount} items`}
              >
                <svg
                  className="w-5 h-5 sm:w-6 sm:h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 3h2l.4 2M7 13h10l4-8H5.4m0 0L7 13m0 0l-1.5 6M7 13l-1.5 6m0 0h9"
                  />
                </svg>

                {/* Cart Counter Badge */}
                {itemCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-mina-mustard text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center shadow-md animate-in fade-in zoom-in duration-200">
                    {itemCount > 99 ? '99+' : itemCount}
                  </span>
                )}
              </button>

              {/* User Profile - Desktop */}
              <div className="hidden md:block">
                {isAuthenticated && user ? (
                  <ProfileMenu user={user} onLogout={handleLogout} />
                ) : (
                  <button
                    onClick={handleLoginClick}
                    className="flex items-center gap-2 bg-black/10 hover:bg-black/20 px-3 py-2 rounded-mina text-mina-espresso transition-all active:scale-95"
                  >
                    <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-black/10 flex items-center justify-center">
                      <svg
                        className="w-4 h-4 sm:w-5 sm:h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                        />
                      </svg>
                    </div>
                    <span className="text-sm font-medium hidden lg:inline">Sign in</span>
                  </button>
                )}
              </div>

              {/* Mobile menu button */}
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="lg:hidden p-2 text-mina-espresso hover:bg-black/10 rounded-mina transition-all active:scale-95"
                aria-label="Toggle menu"
                aria-expanded={isMenuOpen}
              >
                <svg
                  className="h-5 w-5 sm:h-6 sm:w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  {isMenuOpen ? (
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  ) : (
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 6h16M4 12h16M4 18h16"
                    />
                  )}
                </svg>
              </button>
            </div>
          </div>

          {/* Mobile Navigation Menu */}
          {isMenuOpen && (
            <div className="lg:hidden border-t border-mina-espresso/20 pb-4 animate-in slide-in-from-top duration-200">
              <nav className="pt-4 space-y-1">
                {/* Mobile User Profile */}
                {isAuthenticated && user ? (
                  <>
                    <div className="px-4 py-3 border-b border-mina-espresso/20">
                      <p className="text-sm font-medium text-mina-espresso">{user.name}</p>
                      <p className="text-xs text-mina-espresso/60">{user.phone || user.email}</p>
                    </div>
                    <Link
                      to="/profile"
                      onClick={() => setIsMenuOpen(false)}
                      className="flex items-center gap-3 px-4 py-3 rounded-mina text-sm font-medium text-mina-espresso hover:bg-black/10 transition-all"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                        />
                      </svg>
                      Profile
                    </Link>
                    <Link
                      to="/orders"
                      onClick={() => setIsMenuOpen(false)}
                      className="flex items-center gap-3 px-4 py-3 rounded-mina text-sm font-medium text-mina-espresso hover:bg-black/10 transition-all"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                        />
                      </svg>
                      Orders
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="flex items-center gap-3 px-4 py-3 rounded-mina text-sm font-medium text-mina-espresso hover:bg-black/10 transition-all w-full"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                        />
                      </svg>
                      Logout
                    </button>
                  </>
                ) : (
                  <button
                    onClick={handleLoginClick}
                    className="flex items-center gap-3 px-4 py-3 rounded-mina text-sm font-medium text-mina-espresso hover:bg-black/10 transition-all w-full"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                      />
                    </svg>
                    Sign in
                  </button>
                )}
              </nav>
            </div>
          )}
        </div>
      </header>

      {/* Cart Drawer */}
      <CartDrawer isOpen={isCartOpen} onClose={closeCart} />
    </>
  );
}
